import { ButtonType, ButtonWidget, DropdownButtonType, List, ListItem, SingleSelectDropdown, TabStyles } from '@tableau/widgets';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { BridgeSiteSettingsProps } from '~src/settings/bridge-settings/BridgeSiteSettings';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { componentRegistryEntry } from '~src/settings/general-settings/components/SettingsComponentRegistryFunctions';
import { cssClass } from '~src/style';

import { PoolAssignmentOption } from '../types/PoolAssignmentOption';
import { createPoolOptionsFromPools } from '../utils/PoolModelUtilities';
import { findDomainsMissingDomainWideRule } from '../utils/RoutingFilterWarningUtilities';
import { BridgeRoutingFiltersInformation } from './BridgeRoutingFiltersInformation';
import { DomainGroupedRoutingFilterView } from './domainGroupedView/DomainGroupedRoutingFilterView';
import { PermissionGroupedRoutingFilterView } from './permissionGroupedView/PermissionGroupedRoutingFilterView';
import { PoolGroupedRoutingFilterView } from './poolGroupedView/PoolGroupedRoutingFilterView';
import { mapResponseToRoutingFilterItems, mapUnsavedFilterToRoutingFilterItem } from './routingFilterMappers';
import { RoutingFiltersActionsContext, RoutingFiltersActionsContextValue } from './RoutingFiltersActionsContext';
import { RoutingFiltersConflictContext, RoutingFiltersConflictContextValue, UnsavedFilter } from './RoutingFiltersConflictContext';
import { RoutingFiltersWarningContext, RoutingFiltersWarningContextValue } from './RoutingFiltersWarningContext';
import { RoutingFilterTable } from './routingFilterTable/RoutingFilterTable';
import { useRoutingFilterHandlers } from './useRoutingFilterHandlers';
import { useRoutingFilters } from './useRoutingFilters';

enum ViewMode {
  ROUTING_FILTERS = 'routing_filters',
  POOLS = 'pools',
  DOMAINS = 'domains',
  PERMISSION_TYPE = 'permission_type',
}

const VALID_VIEW_MODES: Set<string> = new Set([
  ViewMode.ROUTING_FILTERS, ViewMode.POOLS, ViewMode.DOMAINS, ViewMode.PERMISSION_TYPE,
]);

export const BridgeRoutingFiltersSpecs = componentRegistryEntry({
  name: 'BridgeRoutingFilters',
  component: BridgeRoutingFilters
});

export function BridgeRoutingFilters(props: BridgeSiteSettingsProps) {
  const { data: rawFilters = [], isLoading: isLoadingFilters, isError: isFiltersError } = useRoutingFilters(props.settings.id);
  const isLoading = isLoadingFilters || !props.hasLoadedEdgePools;

  const userDefinedPools = props.edgePools.userDefinedPools;
  const queryClient = useQueryClient();
  const poolOptions: ReadonlyArray<PoolAssignmentOption> = React.useMemo(
    () => createPoolOptionsFromPools(userDefinedPools),
    [userDefinedPools]
  );

  const [unsavedFilter, setUnsavedFilter] = React.useState<UnsavedFilter | null>(null);
  // Conflicting rows stay un-highlighted until the user opens the conflict badge
  // on the unsaved row. Reset back to false whenever a new conflict is reported
  // (see onConflict) so a fresh conflict reveals only the unsaved row first.
  // Clearing the unsaved filter also resets this (see clearUnsavedFilter).
  const [conflictsHighlighted, setConflictsHighlighted] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<ViewMode>(ViewMode.ROUTING_FILTERS);

  const showConflictingRoutingFilters = React.useCallback(() => {
    setConflictsHighlighted(true);
  }, []);

  React.useEffect(() => {
    if (isFiltersError) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.bridgeSiteSettingsRoutingFiltersLoadError(),
      });
    }
  }, [isFiltersError]);

  const routingFilters = React.useMemo(() => {
    const items = mapResponseToRoutingFilterItems(rawFilters, userDefinedPools);
    if (!unsavedFilter) {
      return items;
    }
    const unsavedItem = mapUnsavedFilterToRoutingFilterItem(unsavedFilter, userDefinedPools);
    if (unsavedFilter.isNew) {
      return [unsavedItem, ...items];
    }
    return items.map(item => item.id === unsavedItem.id ? unsavedItem : item);
  }, [rawFilters, userDefinedPools, unsavedFilter]);

  // Domains missing a domain-wide (all-projects) rule. Shared by the row warning
  // badges and the reopened dialog's missing-domain-wide banner.
  const warnedDomains = React.useMemo(
    () => findDomainsMissingDomainWideRule(routingFilters),
    [routingFilters]
  );

  const clearUnsavedFilter = React.useCallback(() => {
    setUnsavedFilter(null);
    setConflictsHighlighted(false);
  }, []);

  // Create/edit/delete click handlers plus their React Query cache write-through
  // live in this hook; the component keeps only the unsaved-filter state they
  // read and update (routingFilters is derived from it above).
  const { onEditFilterClicked, onDeleteFilterClicked, onAddFilterClicked, onClearUnspecifiedProjectsClicked } = useRoutingFilterHandlers({
    queryClient,
    siteId: props.settings.id,
    rawFilters,
    routingFilters,
    poolOptions,
    warnedDomains,
    unsavedFilter,
    setUnsavedFilter,
    setConflictsHighlighted,
  });

  const actionsContextValue = React.useMemo<RoutingFiltersActionsContextValue>(
    () => ({ onEditFilterClicked, onDeleteFilterClicked, onAddFilterClicked, onClearUnspecifiedProjectsClicked }),
    [onEditFilterClicked, onDeleteFilterClicked, onAddFilterClicked, onClearUnspecifiedProjectsClicked]
  );

  const conflictContextValue = React.useMemo<RoutingFiltersConflictContextValue>(() => ({
    unsavedFilter,
    clearUnsavedFilter,
    conflictsHighlighted,
    showConflictingRoutingFilters,
  }), [unsavedFilter, clearUnsavedFilter, conflictsHighlighted, showConflictingRoutingFilters]);

  const warningContextValue = React.useMemo<RoutingFiltersWarningContextValue>(() => ({
    warnedDomains,
  }), [warnedDomains]);

  const routingFiltersOptionLabel = Messages.bridgeSiteSettingsRoutingFilters();
  const poolsOptionLabel = Messages.bridgeSiteSettingsViewByPoolsOption();
  const domainsOptionLabel = Messages.bridgeSiteSettingsViewByDomainsOption();
  const permissionTypeOptionLabel = Messages.bridgeSiteSettingsRoutingFilterPermissionType();

  const labelByMode: Record<ViewMode, string> = {
    [ViewMode.ROUTING_FILTERS]: routingFiltersOptionLabel,
    [ViewMode.POOLS]: poolsOptionLabel,
    [ViewMode.DOMAINS]: domainsOptionLabel,
    [ViewMode.PERMISSION_TYPE]: permissionTypeOptionLabel,
  };
  const selectedOptionLabel = labelByMode[viewMode];

  const viewModeLabel = `${Messages.bridgeSiteSettingsViewByPrefix()} ${selectedOptionLabel}`;

  const viewModeListItems: ListItem[] = React.useMemo(() => [
    { value: 'header', content: Messages.bridgeSiteSettingsViewByPrefix(), disabled: true },
    { value: ViewMode.ROUTING_FILTERS, content: routingFiltersOptionLabel },
    { value: ViewMode.POOLS, content: poolsOptionLabel },
    { value: ViewMode.DOMAINS, content: domainsOptionLabel },
    { value: ViewMode.PERMISSION_TYPE, content: permissionTypeOptionLabel },
  ], [routingFiltersOptionLabel, poolsOptionLabel, domainsOptionLabel, permissionTypeOptionLabel]);

  const onViewModeChange = React.useCallback((value: string) => {
    setViewMode(VALID_VIEW_MODES.has(value) ? value as ViewMode : ViewMode.ROUTING_FILTERS);
  }, []);

  return (
    <RoutingFiltersActionsContext.Provider value={actionsContextValue}>
    <RoutingFiltersConflictContext.Provider value={conflictContextValue}>
    <RoutingFiltersWarningContext.Provider value={warningContextValue}>
      <SettingsSection title={Messages.bridgeSiteSettingsRoutingFilters()} testId={'bridge-routing-filters'}>
        <BridgeRoutingFiltersInformation />
        <table className={containerTableClass} role={'presentation'}>
          <tbody>
            <tr>
              <td className={toolbarCellClass}>
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  onClick={() => onAddFilterClicked()}
                  disabled={!props.hasLoadedEdgePools || !!unsavedFilter}
                  testId={'add-routing-filter-button'}
                >
                  {`+ ${Messages.bridgeSiteSettingsAddRoutingFilter()}`}
                </ButtonWidget>
              </td>
              <td className={toolbarSpacerClass} />
              <td className={toolbarCellRightClass}>
                <SingleSelectDropdown
                  testId={'routing-filter-view-mode-dropdown'}
                  type={DropdownButtonType.Outline}
                  list={<List items={viewModeListItems} />}
                  value={viewMode}
                  onChange={onViewModeChange}
                  label={''}
                >
                  {viewModeLabel}
                </SingleSelectDropdown>
              </td>
            </tr>
          </tbody>
        </table>
        {viewMode === ViewMode.ROUTING_FILTERS && (
          <RoutingFilterTable
            filters={routingFilters}
            isLoading={isLoading}
          />
        )}
        {viewMode === ViewMode.POOLS && (
          <PoolGroupedRoutingFilterView
            filters={routingFilters}
            poolOptions={poolOptions}
            isLoading={isLoading}
          />
        )}
        {viewMode === ViewMode.DOMAINS && (
          <DomainGroupedRoutingFilterView
            filters={routingFilters}
            isLoading={isLoading}
          />
        )}
        {viewMode === ViewMode.PERMISSION_TYPE && (
          <PermissionGroupedRoutingFilterView
            filters={routingFilters}
            isLoading={isLoading}
          />
        )}
      </SettingsSection>
    </RoutingFiltersWarningContext.Provider>
    </RoutingFiltersConflictContext.Provider>
    </RoutingFiltersActionsContext.Provider>
  );
}

const containerTableClass = cssClass('bridge-routing-filters', 'container-table', {
  width: '100%',
  borderSpacing: 0,
  marginTop: TabStyles.Spacing.DefaultSpace,
});

const toolbarCellClass = cssClass('bridge-routing-filters', 'toolbar-cell', {
  paddingBottom: TabStyles.Spacing.DefaultSpace,
  whiteSpace: 'nowrap',
});

const toolbarSpacerClass = cssClass('bridge-routing-filters', 'toolbar-spacer', {
  width: '100%',
});

const toolbarCellRightClass = cssClass('bridge-routing-filters', 'toolbar-cell-right', {
  paddingBottom: TabStyles.Spacing.DefaultSpace,
  whiteSpace: 'nowrap',
  textAlign: 'right',
});
